import { useMemo, useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { PRODUCTS, SITE } from "../lib/site";
import { ArrowUpRight, Filter, Play, Pause, Sparkles } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

function platformLabel(p) {
  return p === "unlockt" ? "Unlockt.me" : p === "clips4sale" ? "Clips4sale" : "iWantClips";
}
// If a product has its own direct file URL, use it; otherwise fall back
// to the platform storefront root.
function productLink(p) {
  return p.url && p.url.trim() ? p.url : (SITE.external[p.platform] || SITE.external.unlockt);
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "audio", label: "Audio" },
  { key: "video", label: "Video" },
];

export default function Shop() {
  usePageMeta({title:"Shop - Erotic Hypnosis MP3s & Fly-on-the-Wall Videos",description:"Recorded erotic hypnosis videos and MP3s by Queen Cleopatra. Delivered via Unlockt.me. Adults 18+.",path:"/shop"});
  const [filter, setFilter] = useState("all");
  const [playingId, setPlayingId] = useState(null);
  const items = useMemo(() => filter === "all" ? PRODUCTS : PRODUCTS.filter(p => p.type === filter), [filter]);

  return (
    <div data-testid="page-shop">
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="eyebrow mb-5">The Shop</div>
        <h1 className="font-serif text-5xl md:text-7xl text-cream leading-[1] max-w-3xl">
          Sound and image for <em className="text-gold">private hours</em>.
        </h1>
        <p className="mt-6 max-w-2xl text-cream/70 leading-relaxed">
          Each item is delivered through a trusted external platform. Click any cover to be taken to the relevant store - Unlockt.me, Clips4sale, or iWantClips - for secure purchase and download.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-24">
        <div className="flex items-center gap-3 mb-10 border-b border-[#c9a04b]/15 pb-5">
          <Filter size={14} className="text-gold" />
          <span className="eyebrow">Filter</span>
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`shop-filter-${f.key}`}
              className={`text-[11px] uppercase tracking-[0.22em] px-4 py-2 border transition-colors ${
                filter === f.key ? "border-gold text-gold" : "border-[#c9a04b]/25 text-cream/60 hover:text-cream"
              }`}
            >{f.label}</button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#c9a04b]/15">
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={`shop-product-${p.id}`}
              className="bg-charcoal hover:bg-charcoalMuted transition-colors p-6 flex flex-col relative"
            >
              {p.pending && (
                <div
                  data-testid={`shop-badge-pending-${p.id}`}
                  className="absolute top-3 right-3 z-10 text-[9px] uppercase tracking-[0.24em] bg-charcoal/90 border border-gold/50 text-gold px-2.5 py-1"
                >
                  Awaiting Review
                </div>
              )}
              {p.pending ? (
                <div
                  data-testid={`shop-cover-${p.id}`}
                  className="block aspect-[4/5] overflow-hidden mb-6 border border-[#c9a04b]/15 cursor-not-allowed"
                >
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover opacity-60" />
                </div>
              ) : (
                <a
                  href={productLink(p)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`shop-cover-${p.id}`}
                  className="group block aspect-[4/5] overflow-hidden mb-6 border border-[#c9a04b]/15"
                >
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </a>
              )}
              <div className="eyebrow mb-2">{p.kind}{p.duration ? ` · ${p.duration}` : ""}</div>
              <h3 className="font-serif text-2xl text-cream mb-3 leading-snug">{p.title}</h3>
              <p className="text-sm text-cream/65 leading-relaxed mb-4 flex-1">{p.description}</p>

              {/* Inline audio preview + spiral CTA (only if provided) */}
              {(p.audioPreview || p.spiralUrl) && (
                <div className="mb-4 flex flex-wrap items-center gap-2" data-testid={`shop-extras-${p.id}`}>
                  {p.audioPreview && (
                    <ProductAudio
                      productId={p.id}
                      src={p.audioPreview}
                      playing={playingId === p.id}
                      onToggle={(next) => setPlayingId(next ? p.id : null)}
                    />
                  )}
                  {p.spiralUrl && (
                    <Link
                      to={p.spiralUrl}
                      data-testid={`shop-spiral-${p.id}`}
                      className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] px-3 py-2 border border-[#c9a04b]/40 text-gold hover:bg-gold hover:text-charcoal transition-colors"
                    >
                      <Sparkles size={12} /> Open with Spiral
                    </Link>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between pt-5 border-t border-[#c9a04b]/15">
                <span className="text-gold text-sm">{p.price}</span>
                {p.pending ? (
                  <span
                    data-testid={`shop-pending-note-${p.id}`}
                    className="text-[10px] uppercase tracking-[0.22em] text-cream/40"
                  >
                    Available soon
                  </span>
                ) : p.custom ? (
                  <Link
                    to="/book"
                    data-testid={`shop-book-${p.id}`}
                    className="text-[10px] uppercase tracking-[0.22em] text-cream/60 hover:text-gold inline-flex items-center gap-1"
                  >
                    Enquire to Book <ArrowUpRight size={11} />
                  </Link>
                ) : (
                  <a
                    href={productLink(p)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] uppercase tracking-[0.22em] text-cream/60 hover:text-gold inline-flex items-center gap-1"
                  >
                    {platformLabel(p.platform)} <ArrowUpRight size={11} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-cream/40 mt-8 max-w-2xl tracking-wider">
          Purchases are processed by external platforms - no card data passes through this site. Download links and terms are governed by the relevant platform.
        </p>
      </section>
    </div>
  );
}

// Inline audio preview for a single product. Only one product plays at a time
// (controlled by the parent via `playing` + `onToggle`).
function ProductAudio({ productId, src, playing, onToggle }) {
  const audioRef = useRef(null);
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.play().catch(() => onToggle(false));
    } else {
      a.pause();
    }
  }, [playing, onToggle]);

  return (
    <div className="inline-flex items-center gap-2 border border-[#c9a04b]/30 pl-1 pr-3 py-1">
      <button
        type="button"
        onClick={() => onToggle(!playing)}
        data-testid={`shop-audio-toggle-${productId}`}
        aria-label={playing ? "Pause preview" : "Play preview"}
        className="w-8 h-8 flex items-center justify-center text-gold hover:text-cream transition-colors"
      >
        {playing ? <Pause size={12} /> : <Play size={12} />}
      </button>
      <span className="text-[10px] uppercase tracking-[0.22em] text-cream/70">Preview</span>
      <audio
        ref={audioRef}
        src={src}
        onEnded={() => onToggle(false)}
        preload="none"
        data-testid={`shop-audio-el-${productId}`}
      />
    </div>
  );
}
